/**
 * Handler logic for Navigation Error.
 * This file encapsulates a shared platform-level reaction to a framework or navigation event.
 */
import { inject } from '@angular/core';
import { NavigationError, RedirectCommand, Router } from '@angular/router';

import { UiToastService } from '../../shared/ui/ui-toast/ui-toast.service';

const CHUNK_LOAD_TOAST_WINDOW_MS = 3000;

let lastChunkLoadToastAt = 0;

export function handleNavigationError(error: NavigationError): RedirectCommand | void {
  if (!isLazyChunkLoadError(error.error)) {
    return;
  }

  const toast = inject(UiToastService);
  const router = inject(Router);
  const now = Date.now();

  if (now - lastChunkLoadToastAt > CHUNK_LOAD_TOAST_WINDOW_MS) {
    lastChunkLoadToastAt = now;
    toast.error(
      'Unable to open page',
      getChunkLoadMessage(),
      5000
    );
  }

  return new RedirectCommand(router.parseUrl(router.url), { replaceUrl: true });
}

function isLazyChunkLoadError(error: unknown): boolean {
  const name = getErrorProperty(error, 'name').toLowerCase();
  const message = getErrorProperty(error, 'message').toLowerCase();

  return (
    name === 'chunkloaderror' ||
    message.includes('loading chunk') ||
    message.includes('failed to fetch dynamically imported module') ||
    message.includes('err_internet_disconnected')
  );
}

function getChunkLoadMessage(): string {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return 'This page section could not load because you are offline. Please reconnect and try again.';
  }

  return 'This page section could not load. Please check your internet connection and try again.';
}

function getErrorProperty(error: unknown, property: 'message' | 'name'): string {
  if (error instanceof Error) {
    return error[property] ?? '';
  }

  if (error && typeof error === 'object') {
    const value = (error as Record<string, unknown>)[property];
    return typeof value === 'string' ? value : '';
  }

  return typeof error === 'string' && property === 'message' ? error : '';
}
